import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { BarChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Bar } from 'recharts';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { FilteringTag } from 'jbc-front/components/SearchForm';
import Loading from 'src/components/Loading';
import { payLoad, generateExpensesSummaryChartData, changeChartLanguage } from 'src/utils/Dashboard';
import { setDashboardData, getChartData, fetchExpenseSummary } from 'src/reducers/dashboard';

import CustomizedLegend from './CustomizedLegend';
import { TitleBar, Container, Chart, Row, Col, Wrapper } from '../styles/ExpenseSummaryStyle';
import ExpenseSummaryYearSelector from './ExpenseSummaryYearSelector';
import ExpenseSummaryChartTooltips from './ExpenseSummaryChartTooltips';

const ExpenseSummaryChart = ({ yearLists, expensesSummary, setChart, chartData, fetchExpenseSummaryData }) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState({
    compareYear: yearLists[0],
    targetYear: yearLists[yearLists.length - 1]
  });
  const [viewMode, setViewMode] = useState('Monthly');
  const [disabledItems, setDisabledItems] = useState([]);
  const [reverse, setReverse] = useState(false);

  const handleDisableItem = (dataKey, length) => {
    setDisabledItems(prevDisabledItems => {
      const isDisabled = prevDisabledItems.includes(dataKey);

      if (isDisabled) {
        return prevDisabledItems.filter(item => item !== dataKey);
      } else if (prevDisabledItems.length !== length - 1) {
        return [...prevDisabledItems, dataKey];
      }

      return prevDisabledItems;
    });
  };

  useEffect(
    () => {
      if (viewMode === 'Monthly') {
        setChart('expenseSummary', changeChartLanguage(chartData, i18n));
      }
    },
    [i18n.language]
  );

  useEffect(() => {
    setChart('expenseSummary', generateExpensesSummaryChartData(expensesSummary, selectedYear, viewMode));
  }, []);

  const startToFetchExpenseSummary = async option => {
    setIsLoading(true);
    const params = _.isObject(option) ? [option, viewMode] : [selectedYear, option];
    await fetchExpenseSummaryData(...params);
    setDisabledItems([]);
    setIsLoading(false);
  };

  const changeViewMode = mode => {
    if (mode === 'Yearly' && parseInt(selectedYear.targetYear, 10) < parseInt(selectedYear.compareYear, 10)) {
      setSelectedYear({ ...selectedYear, compareYear: selectedYear.targetYear });
    }
    setViewMode(mode);
    startToFetchExpenseSummary(mode);
  };

  const handleChangeYear = event => {
    const { value, name } = event.target;
    const newSelectedYear = { ...selectedYear, [name]: value };
    setSelectedYear(newSelectedYear);
    startToFetchExpenseSummary(newSelectedYear);
  };

  return (
    <Wrapper className="u-mt30">
      <TitleBar>{t('features.Dashbord.components.ExpenseSummary.Title')}</TitleBar>

      <Container>
        <Row>
          <Col>
            <FilteringTag key="Monthly" selected={viewMode === 'Monthly'} onClick={() => changeViewMode('Monthly')}>
              {t('features.Dashbord.components.ExpenseSummary.Monthly_Button')}
            </FilteringTag>
            <FilteringTag key="Yearly" selected={viewMode === 'Yearly'} onClick={() => changeViewMode('Yearly')}>
              {t('features.Dashbord.components.ExpenseSummary.Yearly_Button')}
            </FilteringTag>
          </Col>

          <div id="selected-year-section">
            <ExpenseSummaryYearSelector viewMode={viewMode} yearLists={yearLists} selectedYear={selectedYear} handleChangeYear={handleChangeYear} />
          </div>
        </Row>

        {isLoading ? (
          <Loading />
        ) : (
          <Chart>
            <ResponsiveContainer width="99%" height={240}>
              <BarChart data={chartData?.data} margin={{ top: 20, right: 55, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  domain={[0, 23]}
                  interval={0}
                  dataKey={viewMode === 'Monthly' ? 'month' : 'year'}
                  padding={{ top: 20, bottom: 20 }}
                  tickCount={10}
                  fontSize={'12px'}
                />
                <YAxis type="number" tick={{ fontSize: 12 }} />

                <Tooltip content={<ExpenseSummaryChartTooltips />} />
                {_.toPairs(chartData?.colors)
                  .filter(([payrollRuleGroupName]) => !_.includes(disabledItems, payrollRuleGroupName))
                  .map(([payrollRuleGroupName, color]) => <Bar stackId="a" key={payrollRuleGroupName} dataKey={payrollRuleGroupName} fill={color} />)}
              </BarChart>
            </ResponsiveContainer>
            <CustomizedLegend
              payload={payLoad(chartData?.colors, reverse)}
              setDisabledItems={setDisabledItems}
              setReverse={setReverse}
              reverse={reverse}
              disabledItems={disabledItems}
              handleClick={handleDisableItem}
            />
          </Chart>
        )}
      </Container>
    </Wrapper>
  );
};

const mapDispatchToProps = {
  setChart: setDashboardData,
  fetchExpenseSummaryData: fetchExpenseSummary
};

const mapStateToProps = state => ({
  chartData: getChartData(state, 'expenseSummary')
});

Tooltip.propTypes = {
  allowEscapeViewBow: PropTypes.shape({
    x: PropTypes.bool,
    y: PropTypes.bool
  })
};

export default connect(mapStateToProps, mapDispatchToProps)(ExpenseSummaryChart);
